import { useContext, useEffect, useRef, useState } from 'react'
import { useFormikContext, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { FormContent, FormHeader, FormError, PhoneInput, ButtonDrawer } from '~/components'
import { StepperContext } from '~/components/ui/ViewStepper'
import { StoreContext } from '~/store'
import { wrapWithValidation } from '~/utils'
import { initiateActivation, initiateGoogleSignIn, initiateAppleSignIn, validateOidcToken, completeActivation } from '~/lib/api'
import { emitCustomEvent } from 'react-custom-events'
import { useTranslation, Trans } from 'react-i18next'
import i18next from '~/i18n'

function loadGIS() {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) { resolve(); return }
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = resolve
    script.onerror = reject
    document.head.appendChild(script)
  })
}

function loadAppleSignIn() {
  return new Promise((resolve, reject) => {
    if (window.AppleID) { resolve(); return }
    const script = document.createElement('script')
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
    script.async = true
    script.onload = resolve
    script.onerror = reject
    document.head.appendChild(script)
  })
}

export default function SelectMethod({ fullscreen }) {
  const { t } = useTranslation()
  const { prev, next, parentContext } = useContext(StepperContext)
  const { config, loginAndFetchBookings } = useContext(StoreContext)
  const { values, setFieldValue, setFieldError } = useFormikContext()
  const [isPhoneLoading, setIsPhoneLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isAppleLoading, setIsAppleLoading] = useState(false)
  const [googleError, setGoogleError] = useState(null)
  const [appleError, setAppleError] = useState(null)
  const inputRef = useRef()
  const googleOverlayRef = useRef()
  const googleInnerRef = useRef()
  // SOCIAL_LOGIN_ENABLED is the kill switch: with it off, neither provider is offered
  // and neither SDK is loaded, whatever client IDs happen to be configured. A client ID
  // is still required on top, so a provider we have no credentials for stays hidden.
  const socialLoginEnabled = typeof CONFIG !== 'undefined' && !!CONFIG.social_login_enabled
  const googleLoginEnabled = socialLoginEnabled && !!CONFIG.google_client_id
  const appleLoginEnabled = socialLoginEnabled && !!CONFIG.apple_client_id

  // Phone flow
  const makePhoneRequest = () => {
    setIsPhoneLoading(true)
    initiateActivation(values.dialCode, values.phone)
      .then(response => {
        if (response?.deviceActivationState === 'PHONE_OTP_INITIATED') {
          next('validatePhone')
        }
      })
      .catch(error => setFieldError('phone', error?.response?.message))
      .finally(() => setIsPhoneLoading(false))
  }
  const wrappedNext = wrapWithValidation(makePhoneRequest, useFormikContext())

  // After auto-login (no consent needed), navigate based on context
  const handleLoginComplete = (userId) => {
    loginAndFetchBookings(userId)
    if (fullscreen) {
      parentContext?.next('confirm')
    } else {
      emitCustomEvent('SHOW_LOGIN', false)
    }
  }

  // Google credential handler (called by GIS after OAuth)
  const handleGoogleCredential = async (credentialResponse) => {
    setIsGoogleLoading(true)
    setGoogleError(null)
    try {
      await initiateGoogleSignIn()
      const result = await validateOidcToken(credentialResponse.credential)

      if (result.shouldConsentToMarketing !== undefined) {
        setFieldValue('shouldConsentToMarketing', result.shouldConsentToMarketing, false)
      }
      if (result.shouldConsentToTermsAndConditions !== undefined) {
        setFieldValue('shouldConsentToTermsAndConditions', result.shouldConsentToTermsAndConditions, false)
      }
      if (result.externalEmail) setFieldValue('externalEmail', result.externalEmail, false)
      if (result.userInfo?.firstName) setFieldValue('firstName', result.userInfo.firstName, false)
      if (result.userInfo?.lastName) setFieldValue('lastName', result.userInfo.lastName, false)

      const state = result.deviceActivationState
      console.log('[Google] validate-oidc-token result:', result)

      if (result.userId && (state === 'ACTIVATION_COMPLETE_EXISTING_USER' || state === 'ACTIVATION_COMPLETE_NEW_USER')) {
        // Server completed activation immediately (token already in localStorage)
        handleLoginComplete(result.userId)
      } else if (state === 'EXTERNAL_LOGIN_COMPLETE_EXISTING_USER') {
        // ADD_PHONE has to win: activation can't complete without a phone number.
        // Marketing consent is collected after login by MarketingConsentModal, so
        // only an outstanding terms consent still needs the acceptTerms screen.
        if (result.expectedActions?.includes('ADD_PHONE')) {
          next('socialAddPhone')
        } else if (result.shouldConsentToTermsAndConditions) {
          next('acceptTerms')
        } else {
          const activation = await completeActivation({ termsAndConditions: true })
          if (activation.userId) {
            handleLoginComplete(activation.userId)
          }
        }
      } else if (state === 'EXTERNAL_LOGIN_COMPLETE_NEW_USER') {
        next('socialAddPhone')
      } else {
        console.warn('[Google] Unhandled deviceActivationState:', state, result)
        setGoogleError(t('error-google-signin-failed'))
      }
    } catch (err) {
      console.error(err)
      setGoogleError(err?.response?.message || t('error-google-signin-failed'))
    } finally {
      setIsGoogleLoading(false)
    }
  }

  // Stretch whatever GIS produced over the full area of our own button.
  const applyGoogleScale = () => {
    const overlay = googleOverlayRef.current
    const inner = googleInnerRef.current
    if (!overlay || !inner) return

    const width = overlay.offsetWidth
    const height = overlay.offsetHeight
    const rendered = inner.offsetWidth
    const renderedHeight = inner.offsetHeight
    if (!width || !height || !rendered || !renderedHeight) return

    inner.style.transform = `scale(${width / rendered}, ${height / renderedHeight})`
  }

  // GIS renders its own button into an invisible overlay so we can show our own
  // design underneath. It clamps the width to 200-400px and picks its own height,
  // so the result almost never matches our button - it has to be stretched, or
  // the parts of our button it doesn't reach have nothing behind them to click.
  const renderGoogleButton = () => {
    const overlay = googleOverlayRef.current
    const inner = googleInnerRef.current
    if (!overlay || !inner || !window.google?.accounts?.id) return

    const width = overlay.offsetWidth
    const height = overlay.offsetHeight
    if (!width || !height) return // Not laid out yet - the observer calls back

    inner.style.transform = 'none'
    inner.innerHTML = ''
    window.google.accounts.id.renderButton(inner, {
      theme: 'outline',
      size: 'large',
      width: Math.min(Math.max(Math.round(width), 200), 400),
      text: 'continue_with',
      shape: 'rectangular',
    })

    applyGoogleScale()
  }

  // Load GIS on mount and render invisible overlay button
  useEffect(() => {
    if (!googleLoginEnabled) return
    let observer
    let innerObserver

    loadGIS()
      .then(() => {
        const clientId = CONFIG.google_client_id
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleCredential,
          use_fedcm_for_prompt: false,
        })
        // Re-render on every size change. The first callback arrives once the
        // overlay has been laid out, which is what we used to miss: measuring at
        // mount gave 0 and silently fell back to a fixed 360px wide button.
        if (typeof ResizeObserver !== 'undefined' && googleOverlayRef.current && googleInnerRef.current) {
          observer = new ResizeObserver(renderGoogleButton)
          observer.observe(googleOverlayRef.current)
          // GIS injects its button asynchronously, so rescale whenever the
          // rendered size actually lands. A transform doesn't change layout size,
          // so this can't feed itself.
          innerObserver = new ResizeObserver(applyGoogleScale)
          innerObserver.observe(googleInnerRef.current)
        }
        else renderGoogleButton()
      })
      .catch(err => console.error('GIS load failed', err))

    return () => {
      observer?.disconnect()
      innerObserver?.disconnect()
    }
  }, [])

  // Load Apple Sign-In SDK on mount
  useEffect(() => {
    if (!appleLoginEnabled) return
    loadAppleSignIn()
      .then(() => {
        window.AppleID.auth.init({
          clientId: CONFIG.apple_client_id,
          scope: 'name email',
          redirectURI: `${window.location.origin}/auth/apple/callback`,
          usePopup: true,
        })
      })
      .catch(err => console.error('Apple Sign-In load failed', err))
  }, [])

  const handleAppleSignIn = async () => {
    setIsAppleLoading(true)
    setAppleError(null)
    try {
      // signIn() must be called in the same sync tick as the click to avoid
      // Safari popup blocker — run initiate in parallel, not before
      const [response] = await Promise.all([
        window.AppleID.auth.signIn(),
        initiateAppleSignIn(),
      ])
      const idToken = response.authorization.id_token

      // Apple only sends name on first sign-in
      if (response.user?.name?.firstName) {
        setFieldValue('firstName', response.user.name.firstName, false)
      }
      if (response.user?.name?.lastName) {
        setFieldValue('lastName', response.user.name.lastName, false)
      }

      const result = await validateOidcToken(idToken)
      console.log('[Apple] validate-oidc-token result:', result)

      if (result.shouldConsentToMarketing !== undefined) {
        setFieldValue('shouldConsentToMarketing', result.shouldConsentToMarketing, false)
      }
      if (result.shouldConsentToTermsAndConditions !== undefined) {
        setFieldValue('shouldConsentToTermsAndConditions', result.shouldConsentToTermsAndConditions, false)
      }
      if (result.externalEmail) setFieldValue('externalEmail', result.externalEmail, false)
      if (result.userInfo?.firstName) setFieldValue('firstName', result.userInfo.firstName, false)
      if (result.userInfo?.lastName) setFieldValue('lastName', result.userInfo.lastName, false)

      const state = result.deviceActivationState

      if (result.userId && (state === 'ACTIVATION_COMPLETE_EXISTING_USER' || state === 'ACTIVATION_COMPLETE_NEW_USER')) {
        handleLoginComplete(result.userId)
      } else if (state === 'EXTERNAL_LOGIN_COMPLETE_EXISTING_USER') {
        // Same ordering as the Google handler above — see the comment there.
        if (result.expectedActions?.includes('ADD_PHONE')) {
          next('socialAddPhone')
        } else if (result.shouldConsentToTermsAndConditions) {
          next('acceptTerms')
        } else {
          const activation = await completeActivation({ termsAndConditions: true })
          if (activation.userId) handleLoginComplete(activation.userId)
        }
      } else if (state === 'EXTERNAL_LOGIN_COMPLETE_NEW_USER') {
        next('socialAddPhone')
      } else {
        console.warn('[Apple] Unhandled deviceActivationState:', state, result)
        setAppleError(t('error-apple-signin-failed'))
      }
    } catch (err) {
      // User cancelled the popup — not an error
      if (err?.error === 'popup_closed_by_user' || err?.error === 'user_trigger_new_signin_flow' || err?.error === 'popup_blocked_by_browser') return
      console.error(err)
      setAppleError(err?.response?.message || t('error-apple-signin-failed'))
    } finally {
      setIsAppleLoading(false)
    }
  }

  const isLoading = isPhoneLoading || isGoogleLoading || isAppleLoading

  const buttons = [
    ...(fullscreen ? [{ func: prev, isBack: true }] : []),
    { func: wrappedNext, title: t('button-continue'), isLoading: isPhoneLoading, disabled: isLoading },
  ]

  return (
    <>
      <FormContent isLoading={isLoading} fullscreen={fullscreen}>
        <FormHeader>{fullscreen ? t('header-confirm-phone') : t('header-welcome')}</FormHeader>
        <div className="pb-4 flex flex-col gap-4">
          <p className="text-body-lg text-on-surface-variant m-0">
            {fullscreen ? t('info-confirm-phone-login') : t('info-welcome-subtitle')}
          </p>

          <div className="flex flex-col gap-2">
            <p className="text-xs font-bold uppercase tracking-[0.32px] text-on-surface-variant m-0">
              {t('label-phone-number')}
            </p>
            <PhoneInput innerRef={inputRef} onSubmit={wrappedNext} />
            <ErrorMessage name="phone" component={FormError} />
          </div>

          {(googleLoginEnabled || appleLoginEnabled) && <>
            <div className="flex items-center gap-2">
              <hr className="flex-1 border-0 border-t border-outline/40 m-0" />
              <span className="text-xs font-bold uppercase tracking-[0.32px] text-on-surface-variant whitespace-nowrap">
                {t('label-or')}
              </span>
              <hr className="flex-1 border-0 border-t border-outline/40 m-0" />
            </div>

            <div className="flex flex-col gap-3">
              {googleLoginEnabled && (
                <div className="relative">
                  <button
                    className="flex items-center justify-center gap-1.5 h-[50px] min-h-11 rounded-button bg-white text-black text-body-lg font-medium w-full transition-colors cursor-pointer disabled:opacity-50"
                    style={{ border: '1.5px solid #000' }}
                    type="button"
                    disabled={isLoading}
                    aria-label={t('button-login-google')}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <path d="M19.6 10.23c0-.68-.06-1.36-.18-2H10v3.79h5.4a4.6 4.6 0 0 1-2 3.03v2.5h3.22C18.5 15.77 19.6 13.2 19.6 10.23z" fill="#4285F4"/>
                      <path d="M10 20c2.7 0 4.96-.9 6.62-2.45l-3.23-2.5c-.89.6-2.04.95-3.39.95-2.6 0-4.8-1.76-5.6-4.12H1.08v2.58A9.99 9.99 0 0 0 10 20z" fill="#34A853"/>
                      <path d="M4.4 11.88A5.98 5.98 0 0 1 4.08 10c0-.65.11-1.29.32-1.88V5.54H1.08A9.99 9.99 0 0 0 0 10c0 1.61.39 3.14 1.08 4.46l3.32-2.58z" fill="#FBBC04"/>
                      <path d="M10 3.96c1.47 0 2.79.5 3.83 1.5l2.86-2.86C14.96.9 12.7 0 10 0A9.99 9.99 0 0 0 1.08 5.54L4.4 8.12C5.2 5.72 7.4 3.96 10 3.96z" fill="#EA4335"/>
                    </svg>
                    {t('button-login-google')}
                  </button>
                  {/* Invisible GIS-rendered button sits on top to capture clicks and open Google popup */}
                  <div
                    ref={googleOverlayRef}
                    className="absolute inset-0 overflow-hidden opacity-0 cursor-pointer"
                    style={{ pointerEvents: isLoading ? 'none' : 'auto' }}
                    aria-hidden="true"
                  >
                    <div ref={googleInnerRef} className="absolute top-0 left-0 origin-top-left" />
                  </div>
                </div>
              )}

              {appleLoginEnabled && (
                <button
                  className="flex items-center justify-center gap-1.5 h-[50px] min-h-11 rounded-button bg-white text-black text-body-lg font-medium w-full transition-colors cursor-pointer disabled:opacity-50"
                  style={{ border: '1.5px solid #000' }}
                  type="button"
                  disabled={isLoading}
                  onClick={handleAppleSignIn}
                  aria-label={t('button-login-apple')}
                >
                  <svg width="17" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
                  </svg>
                  {t('button-login-apple')}
                </button>
              )}
            </div>

            {googleError && <p className="text-[13px] text-red-600 m-0">{googleError}</p>}
            {appleError && <p className="text-[13px] text-red-600 m-0">{appleError}</p>}
          </>}

          <p className="text-[13px] leading-5 text-center text-on-surface-variant m-0 [&_a]:font-bold [&_a]:text-inherit">
            <Trans
              i18nKey="info-by-continuing-terms"
              components={{ link1: <a href={config.termsLink} target="_blank" rel="noopener noreferrer" /> }}
            />
          </p>
        </div>
      </FormContent>
      <ButtonDrawer buttons={buttons} isLoading={isLoading} fullscreen={fullscreen} />
    </>
  )
}

SelectMethod.validationSchema = Yup.object({
  phone: Yup.string()
    .matches(/^\d+$/, i18next.t('validation-only-numbers'))
    .min(6, i18next.t('validation-length-phone'))
    .max(14, i18next.t('validation-length-phone'))
    .required(i18next.t('validation-need-phone')),
})
